// BUILD-046 task 8.10 (supporting model): the simulated-identity banner and the pilot tools panel (Req 2.5, 15.20).
//
// The banner:
//  - is shown whenever the Caller identity is simulated (`GET /work/me` reports `simulated`), or when the page is known to run against the
//    local pilot even though `/work/me` has not answered (so a failed load never hides the label);
//  - states BOTH that the identity is simulated AND that it is not authentication, visible without any action;
//  - is NOT dismissible: there is no state, no event and no control that hides it.
// The pilot tools panel renders only when `me.simulated` is true, and in S1 holds one thing: a Reset that calls POST /local/reset after a
// confirmation (the reset archives the current pilot data and restores the fictional starting data).
//
// Pure module: no React, no I/O, no runtime imports. Erasable TypeScript only.

export const SIMULATED_IDENTITY_TEXT = 'SIMULATED identity, not authentication';

export interface BannerMe {
  simulated?: boolean;
  identity?: { label?: string } | null;
  employee?: { displayName?: string } | null;
}

export interface BannerView {
  visible: boolean;
  /** Always states both "simulated" and "not authentication". */
  text: string;
  callerName: string | null;
  /** The pilot tools panel is shown only when the server says the identity is simulated. */
  showPilotTools: boolean;
  /** Always false: nothing dismisses the banner. */
  dismissible: false;
}

function statesBoth(label: unknown): label is string {
  return typeof label === 'string' && /simulated/i.test(label) && /not authentication/i.test(label);
}

export function bannerModel(me: BannerMe | null | undefined, options: { localPilot?: boolean } = {}): BannerView {
  const simulated = me?.simulated === true;
  const name = me?.employee?.displayName;
  return {
    visible: simulated || options.localPilot === true,
    // The server's label is used only when it says both things; otherwise the fixed text, so the requirement never depends on the server's wording.
    text: statesBoth(me?.identity?.label) ? me!.identity!.label! : SIMULATED_IDENTITY_TEXT,
    callerName: typeof name === 'string' && name.trim() !== '' ? name : null,
    showPilotTools: simulated,
    dismissible: false,
  };
}

// ───────────────────────── pilot Reset ─────────────────────────

export type ResetPhase = 'idle' | 'confirming' | 'resetting' | 'failed' | 'done';

export interface ResetState {
  phase: ResetPhase;
  /** The failure message, or the archive note after a success. */
  message: string | null;
}

export const RESET_CONFIRM_TEXT = 'Reset the pilot data? The current data is archived and the fictional starting data is restored. The caller goes back to the default person.';
/** The short question the confirmation step asks first (correction cycle 1bb88b3e, item N9); RESET_CONFIRM_TEXT stays as the detail under it. */
export const RESET_QUESTION_TEXT = 'Reset replaces the demo data. Continue?';

export function initialResetState(): ResetState {
  return { phase: 'idle', message: null };
}

/** The Reset link: asks for confirmation. Not while a reset is running. */
export function askReset(state: ResetState): ResetState {
  if (state.phase === 'resetting') return state;
  return { phase: 'confirming', message: null };
}

export function cancelReset(state: ResetState): ResetState {
  return state.phase === 'confirming' ? initialResetState() : state;
}

/** The confirm click. `send` is true exactly once per confirmation; a second click while resetting sends nothing. */
export function confirmReset(state: ResetState): { state: ResetState; send: boolean } {
  if (state.phase !== 'confirming') return { state, send: false };
  return { state: { phase: 'resetting', message: null }, send: true };
}

export function resetSucceeded(state: ResetState, result: { archived?: unknown } | null | undefined): ResetState {
  if (state.phase !== 'resetting') return state;
  const archived = result && typeof result.archived === 'string' && result.archived !== '' ? ` The previous data was archived (${result.archived}).` : '';
  return { phase: 'done', message: `Pilot data reset.${archived}` };
}

export function resetFailed(state: ResetState, thrown: unknown): ResetState {
  if (state.phase !== 'resetting') return state;
  const detail = thrown !== null && typeof thrown === 'object' && typeof (thrown as { message?: unknown }).message === 'string' ? (thrown as { message: string }).message : '';
  return { phase: 'failed', message: `The pilot data was not reset.${detail !== '' ? ` ${detail}` : ''}` };
}

export interface ResetView {
  /** The Reset link is shown when no confirmation or reset is in progress. */
  showLink: boolean;
  showConfirm: boolean;
  confirmText: string;
  busy: boolean;
  message: string | null;
  /** `alert` for a failure, `status` otherwise. */
  messageRole: 'alert' | 'status';
}

export function describeReset(state: ResetState): ResetView {
  return {
    showLink: state.phase === 'idle' || state.phase === 'failed' || state.phase === 'done',
    showConfirm: state.phase === 'confirming',
    confirmText: RESET_CONFIRM_TEXT,
    busy: state.phase === 'resetting',
    message: state.phase === 'resetting' ? 'Resetting…' : state.message,
    messageRole: state.phase === 'failed' ? 'alert' : 'status',
  };
}
